import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import DeptSwitcher, { ccDepartments } from '../components/ContactCenter/DeptSwitcher';
import { useAuth } from '../context/AuthContext';
import AgentTable from '../components/ContactCenter/AgentTable';
import MultiSelect from '../components/ContactCenter/MultiSelect';
import ArcGauge, { fillTowards, fillUnder } from '../components/ContactCenter/ArcGauge';
import SingleSelect from '../components/ContactCenter/SingleSelect';
import { languageOptions, matchesLanguage } from '../components/ContactCenter/language';
import usePersisted, { wasPersisted } from '../components/ContactCenter/usePersisted';
import AgentDetail from '../components/ContactCenter/AgentDetail';
import TeamRollup from '../components/ContactCenter/TeamRollup';
import ExportButton from '../components/ContactCenter/ExportButton';
import { fmtAsOf, fmtCount, fmtClock, fmtPct, STATUS } from '../components/ContactCenter/format';
import { btn, c, card } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Team Performance — the old Director, Manager and
// Supervisor pages as one: for the pods and channels chosen, the gauges, a
// rollup per pod, and the pods' agents grouped and ranked within their pod.
// Everything is computed on the server from stored data
// (services/ccTeam.js); this page draws it.

const DATA_FLOOR = '2026-06-04';
const CHANNELS = ['Voice', 'VIP', 'Callback', 'Email', 'Voicemail', 'Chat'];
const DEFAULT_CHANNELS = ['Voice', 'VIP', 'Callback'];

function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}
const addDays = (ymd, n) => new Date(Date.parse(`${ymd}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const firstOfMonth = (ymd) => `${ymd.slice(0, 8)}01`;
function lastMonth(today) {
  const [y, m] = today.split('-').map(Number);
  return {
    start: new Date(Date.UTC(y, m - 2, 1)).toISOString().slice(0, 10),
    end: new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10),
  };
}

// The old page's windows and its wording, YTD included — it ran from the data
// floor, and said so rather than claiming a calendar year it has no January
// for. Last month and Custom are the portal's, and are kept: the old page had
// no way to look at a closed month on this screen at all.
// The old app's targets, and which way beats them (ui/kpiSpec). Service
// level, Efficiency and QA are met by exceeding; speed of answer, AHT and
// abandon rate by staying under.
const GOALS = {
  serviceLevel: { value: 95, label: '95%' },
  asa:          { value: 30, label: '00:30', lowerIsBetter: true },
  aht:          { value: 300, label: '05:00', lowerIsBetter: true },
  abandonPct:   { value: 3, label: '3%', lowerIsBetter: true },
  efficiency:   { value: 92, label: '92%' },
  adherence:    { value: 90, label: '90%' },
  qa:           { value: 90, label: '90%' },
};

const MODES = [
  ['today-mtd', 'Today vs MTD'],
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['mtd', 'MTD'],
  ['ytd', 'YTD (since Jun 2026)'],
  ['last-month', 'Last Month'],
  ['custom', 'Custom'],
];

// How far past the goal, as the old app put it: a share of the goal, not a
// difference in points — 96.2% against 95% is "1.3% above goal", not 1.2.
// Flipped where lower is better, so beating an AHT target also reads as above.
function beatBy(value, goal) {
  if (!goal || value == null || !(goal.value > 0)) return null;
  const raw = goal.lowerIsBetter ? goal.value - value : value - goal.value;
  return Math.round((raw / goal.value) * 1000) / 10;
}

// STATUS speaks good/watch/bad; the gauge paints good/amber/bad.
const arcTone = (status) => ({ good: 'good', watch: 'amber', bad: 'bad' }[status] || null);


// Director, Manager and Supervisor are one screen at three grants (migration
// 075), exactly as the old app had them: /director and /manager rendered this
// with the same props, and /supervisor ranked the table and opened on ONE pod
// rather than all of them. The figures are identical either way — the variant
// changes what you are looking at first, not what is true.
const TITLES = { director: 'Director', manager: 'Manager', supervisor: 'Supervisor' };

export default function ContactCenterTeam({ variant = 'team' }) {
  const today = pacificToday();
  const { user } = useAuth();
  const view = variant === 'team' ? 'team_performance' : variant;
  const ranked = variant === 'supervisor';
  const [dept, setDept] = useState(() => ccDepartments(user, view)[0]?.id || 'support');
  // Every filter is remembered per view, as the old page remembered them: a
  // supervisor's pod and a director's whole floor are different questions,
  // asked from the same component.
  const key = (name) => `${view}.${name}`;
  const [mode, setMode] = usePersisted(key('range'), 'today-mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });
  // Supervisor opens on one pod, the others on every pod (the old defaults) —
  // unless this browser already holds a choice, which outranks the default.
  const [pods, setPods] = usePersisted(key('selectedPods'), []);   // empty = every pod
  const [pickedFirstPod, setPickedFirstPod] = useState(!ranked || wasPersisted(key('selectedPods')));
  const [channels, setChannels] = usePersisted(key('selectedChannels'), DEFAULT_CHANNELS);
  const [agentScope, setAgentScope] = usePersisted(key('agentScope'), 'all');
  const [language, setLanguage] = usePersisted(key('languageFilter'), '');
  const [search, setSearch] = useState('');
  // The agent table's own pod filter, one pod at a time. Not the same control
  // as the Pod filter in the header: that one is multi-select and rescopes the
  // whole page — the gauges, the rollup and what the server is even asked for.
  // This one narrows the list of agents already on the page and nothing else,
  // which is why it is a single choice and why it lives down here beside the
  // table it acts on.
  const [agentPod, setAgentPod] = usePersisted(key('agentPod'), '');
  const [openId, setOpenId] = useState(null);

  const main = mode === 'last-month' ? lastMonth(today)
    : mode === 'custom' ? (custom.start && custom.end ? custom : null)
      : mode === 'today' ? { start: today, end: today }
        : mode === 'yesterday' ? { start: addDays(today, -1), end: addDays(today, -1) }
          : mode === 'ytd' ? { start: DATA_FLOOR, end: today }
            : { start: firstOfMonth(today), end: today };
  const choice = {
    ...(pods.length ? { pods: pods.join(',') } : {}),
    // Clearing the channel filter means every channel, not the default three:
    // the control says "All", and it has to be true.
    channels: (channels.length ? channels : CHANNELS).join(','),
  };
  const fetchTeam = (range) => api.get('/contact-center/team', { params: { ...range, ...choice, dept } });
  const mainQ = useQuery({
    queryKey: ['cc-team', dept, main?.start, main?.end, choice.pods || '', choice.channels],
    queryFn: () => fetchTeam(main),
    enabled: !!main,
    refetchInterval: 5 * 60 * 1000,
  });
  const todayQ = useQuery({
    queryKey: ['cc-team', dept, today, today, choice.pods || '', choice.channels],
    queryFn: () => fetchTeam({ start: today, end: today }),
    enabled: mode === 'today-mtd',
    refetchInterval: 5 * 60 * 1000,
  });
  // The old gauge's rule: QA follows the range only when the range is a fixed
  // one (custom, yesterday, YTD — and Last Month here). On Today it is month to
  // date, because evaluations are completed after the calls and a morning's
  // own QA is empty. "Today vs MTD" already holds MTD; "Today" asks for it.
  const qaMtd = mode === 'today' || mode === 'today-mtd';
  const mtdQ = useQuery({
    queryKey: ['cc-team', dept, firstOfMonth(today), today, choice.pods || '', choice.channels],
    queryFn: () => fetchTeam({ start: firstOfMonth(today), end: today }),
    enabled: mode === 'today',
    refetchInterval: 5 * 60 * 1000,
  });

  const d = mainQ.data;

  // Supervisor's single-pod default, once the pods are known. Chosen once:
  // clearing the pod filter afterwards means every pod, and must stay that
  // way rather than snapping back on the next render.
  useEffect(() => {
    if (pickedFirstPod || !d?.allPods?.length) return;
    setPods([[...d.allPods].sort((a, b) => a.localeCompare(b))[0]]);
    setPickedFirstPod(true);
  }, [d, pickedFirstPod, setPods]);
  const t = mode === 'today-mtd' ? todayQ.data : d;      // the gauges' period
  // The old page's "custom-like" ranges, which switched its pod rollup to the
  // custom column set and its Hold column to total time on hold. Last Month is
  // ours, and a range like the others.
  const isRange = ['yesterday', 'ytd', 'last-month', 'custom'].includes(mode);
  const periods = useMemo(() => {
    if (!d) return [];
    const byUser = (list) => new Map((list || []).map((a) => [a.userId, a]));
    const mainP = { key: 'main', label: d.range.label, byUser: byUser(d.agents), rollup: d.rollup };
    if (mode !== 'today-mtd') return [mainP];
    return todayQ.data ? [{ key: 'today', label: 'Today', byUser: byUser(todayQ.data.agents), rollup: todayQ.data.rollup }, mainP] : [mainP];
  }, [d, todayQ.data, mode]);

  const needle = search.trim().toLowerCase();
  const worked = (a) => (periods[0]?.byUser.get(a.userId)?.handled || 0) > 0;

  // The options come from every agent the page is holding, NOT from `shown` —
  // a list that re-computed itself against the search box would drop and
  // restore options on each keystroke, and picking a pod that a search had
  // just hidden would be impossible.
  const agentPods = useMemo(
    () => [...new Set((d?.agents || []).map((a) => a.pod).filter(Boolean))].sort((x, y) => x.localeCompare(y)),
    [d],
  );
  // Read through the available list rather than trusted outright. The choice is
  // remembered per person, and the header's pod filter can narrow the page
  // until the remembered pod is no longer on it — at which point this has to
  // read as "all pods" instead of filtering the table down to nothing. Derived
  // rather than reset in an effect: an effect would also fire on first render,
  // while the agents are still loading and every pod looks unavailable, and
  // would clear a choice the reader had just made.
  const podPick = agentPods.includes(agentPod) ? agentPod : '';

  const shown = (d?.agents || [])
    .filter((a) => !podPick || a.pod === podPick)
    .filter((a) => !needle || (a.name || '').toLowerCase().includes(needle))
    .filter((a) => matchesLanguage(a, language))
    .filter((a) => agentScope === 'all' || worked(a));
  const close = useCallback(() => setOpenId(null), []);
  const g = t?.gauges;
  const periodLabel = mode === 'today-mtd' ? 'Today' : d?.range.label;
  const qa = (qaMtd ? (mode === 'today-mtd' ? d : mtdQ.data)?.gauges?.qa : g?.qa)
    || { value: null, evaluations: 0, agents: 0 };
  const qaLabel = qaMtd ? 'MTD' : periodLabel;
  const fc = g?.handled.forecast;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title={(() => {
          const name = TITLES[variant] || 'Team Performance';
          return d?.deptLabel && d.deptLabel !== 'Support' ? `${name} · ${d.deptLabel}` : name;
        })()}
        subtitle={d ? `${d.pods.length === d.allPods.length ? 'All pods' : d.pods.join(', ')} · ${shown.length} agents · ${d.range.label}` : 'Pods and their agents'}
        meta={d?.asOf ? <span style={s.chip}>Data as of {fmtAsOf(d.asOf)}</span> : null}
        actions={(
          <div style={s.row}>
            <DeptSwitcher view={view} value={dept} onChange={(id) => { setDept(id); setPods([]); setPickedFirstPod(!ranked); }} />
            <span role="group" aria-label="Date range" style={s.row}>
            {MODES.map(([key, label]) => (
              <button key={key} type="button" onClick={() => setMode(key)} aria-pressed={mode === key}
                style={mode === key ? s.pillOn : s.pillBtn}>{label}</button>
            ))}
            {mode === 'custom' && (
              <>
                <input id="cc-team-start" type="date" aria-label="Start date" min={DATA_FLOOR} max={today}
                  value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.input} />
                <input id="cc-team-end" type="date" aria-label="End date" min={DATA_FLOOR} max={today}
                  value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.input} />
              </>
            )}
            </span>
          </div>
        )}
      />

      {/* The old page's own filter row: two dropdowns and a two-way switch.
          Nine pods and six channels as pills wrapped the header onto three
          lines, and a wrapped filter is one people stop reading. */}
      <div style={s.filters}>
        {/* emptyMeansAll, because on THIS page an empty pod list is sent as no
            pod filter at all and the server answers for every pod. Without it
            the control read "Pod: No pods" while showing all nine of them —
            the exact opposite of what was happening. */}
        {/* align="left" on all three: s.filters is a LEFT-aligned row, so a
            menu hung off the button's right edge extends leftward, past the
            content's left edge and under the sidebar. This is the "dropdown is
            cut off" the CS team reported. The VP page's copies keep the default
            because they sit in PageHeader's right-aligned actions slot. */}
        <MultiSelect label="Pod" options={d?.allPods || []} selected={pods} onChange={setPods}
          allLabel="All" noun="pods" emptyMeansAll align="left" />
        <MultiSelect label="Channel" options={CHANNELS} selected={channels} onChange={setChannels}
          allLabel="All" noun="channels" emptyMeansAll align="left" />
        <SingleSelect label="Language" options={languageOptions(d?.agents)} value={language}
          onChange={setLanguage} allLabel="All languages" align="left" />
        <span style={s.seg} role="group" aria-label="Which agents">
          {[['all', 'All agents'], ['active', 'Active only']].map(([id, label]) => (
            <button key={id} type="button" aria-pressed={agentScope === id} onClick={() => setAgentScope(id)}
              style={agentScope === id ? s.segOn : s.segOff}>{label}</button>
          ))}
        </span>
      </div>

      {mode === 'custom' && !main && <p style={s.muted}>Choose a start and an end date.</p>}
      {mainQ.isLoading && main && <LoadingSpinner />}
      {mainQ.error && <div style={s.error}>Could not load Team Performance: {mainQ.error.message}</div>}
      {d?.range.clamped && <div style={s.notice}>Contact Center data starts on June 4, 2026; the window starts there.</div>}

      {g && (
        <div style={s.gauges}>
          {/* The old page's six, in its order: service level, speed of answer,
              handled, handle time, efficiency, QA. Adherence and abandon rate
              follow — they are ours, and the old page has no gauge for either
              here. */}
          <ArcGauge title={`Service level · ${periodLabel}`}
            value={g.serviceLevel} display={fmtPct(g.serviceLevel)}
            sublabel={g.slAhtChannels.length ? g.slAhtChannels.join(' + ') : 'No Voice or VIP'}
            tone={arcTone(STATUS.serviceLevel(g.serviceLevel))}
            fill={fillTowards(g.serviceLevel, GOALS.serviceLevel.value)}
            goalFraction={GOALS.serviceLevel.value / 100} goalLabel={GOALS.serviceLevel.value}
            goalText={`Goal ${GOALS.serviceLevel.label}`} beat={beatBy(g.serviceLevel, GOALS.serviceLevel)} />

          <ArcGauge title={`ASA · ${periodLabel}`}
            value={g.asa} display={fmtClock(g.asa)}
            sublabel={(channels.length ? channels : CHANNELS).join(' + ')}
            tone={arcTone(STATUS.asa(g.asa))} fill={fillUnder(g.asa, GOALS.asa.value)}
            goalFraction={1} goalText={`Goal ${GOALS.asa.label}`} beat={beatBy(g.asa, GOALS.asa)} />

          <ArcGauge title={`Handled · ${periodLabel}`}
            value={g.handled.value} display={fmtCount(g.handled.value)}
            sublabel={(channels.length ? channels : CHANNELS).join(' + ')}
            tone={fc?.forecast > 0 ? arcTone(STATUS.serviceLevel((g.handled.value / fc.forecast) * 100)) : null}
            fill={fillTowards(g.handled.value, fc?.forecast)}
            goalFraction={fc?.forecast > 0 ? 1 : null}
            goalText={fc?.forecast > 0 ? `Goal ${fmtCount(fc.forecast)}` : 'No WFM forecast'}
            beat={fc?.vsPct ?? null}
            footnote={g.handled.deduplicated ? null : 'Pods added: a conversation reaching two counts twice'} />

          <ArcGauge title={`AHT · ${periodLabel}`}
            value={g.aht} display={fmtClock(g.aht)}
            sublabel={g.slAhtChannels.length ? g.slAhtChannels.join(' + ') : 'No Voice or VIP'}
            tone={arcTone(STATUS.aht(g.aht))} fill={fillUnder(g.aht, GOALS.aht.value)}
            goalFraction={1} goalText={`Goal ${GOALS.aht.label}`} beat={beatBy(g.aht, GOALS.aht)} />

          <ArcGauge title={`Efficiency % · ${periodLabel}`}
            value={g.efficiency.value} display={fmtPct(g.efficiency.value)}
            tone={arcTone(STATUS.efficiency(g.efficiency.value))}
            fill={fillTowards(g.efficiency.value, GOALS.efficiency.value)}
            goalFraction={GOALS.efficiency.value / 100} goalLabel={GOALS.efficiency.value}
            goalText={`Goal ${GOALS.efficiency.label}`} beat={beatBy(g.efficiency.value, GOALS.efficiency)}
            footnote={`based on ${g.efficiency.agents} of ${g.efficiency.of} agents`
              + (g.efficiency.incomplete ? ` · ${g.efficiency.incomplete} incomplete` : '')} />

          {g.qa && (
            <ArcGauge title={`QA score · ${qaLabel}`}
              value={qa.value} display={qa.value == null ? '—' : `${qa.value.toFixed(1)}%`}
              tone={arcTone(STATUS.qa(qa.value, qa.evaluations))}
              fill={fillTowards(qa.value, GOALS.qa.value)}
              goalFraction={GOALS.qa.value / 100} goalLabel={GOALS.qa.value}
              goalText={`Goal ${GOALS.qa.label}`} beat={beatBy(qa.value, GOALS.qa)}
              // The gauge already goes neutral under three evaluations
              // (STATUS.qa), and said nothing about why. The old app names it —
              // "pilot · n=2" — and that is the part of its QA card worth
              // keeping: a score over one or two evaluations is not a score,
              // and a reader cannot tell that from the number alone. What we
              // do NOT copy is its window: its gauge shows month-to-date for
              // every named range, so picking Last Month or YTD there gives
              // you THIS month's QA and no way to see the period you asked
              // for. Ours means the range it is labelled with.
              footnote={!qa.evaluations ? 'no evaluations completed'
                : qa.evaluations < 3
                  ? `too few to judge · ${qa.evaluations} evaluation${qa.evaluations === 1 ? '' : 's'}`
                  : `based on ${qa.agents} agents · ${fmtCount(qa.evaluations)} evaluations`} />
          )}

          {g.adherence && (
            <ArcGauge title={`Adherence · ${periodLabel}`}
              value={g.adherence.value} display={fmtPct(g.adherence.value)}
              tone={arcTone(STATUS.adherence(g.adherence.value))}
              fill={fillTowards(g.adherence.value, GOALS.adherence.value)}
              goalFraction={GOALS.adherence.value / 100} goalLabel={GOALS.adherence.value}
              goalText={`Goal ${GOALS.adherence.label}`} beat={beatBy(g.adherence.value, GOALS.adherence)}
              footnote={g.adherence.value == null ? 'no WFM schedule in the period'
                : `based on ${g.adherence.agents} of ${g.adherence.of} agents`} />
          )}

          <ArcGauge title={`Abandon rate · ${periodLabel}`}
            value={g.abandonPct} display={fmtPct(g.abandonPct)}
            sublabel={g.slAhtChannels.length ? g.slAhtChannels.join(' + ') : 'No Voice or VIP'}
            tone={arcTone(STATUS.abandonPct(g.abandonPct))} fill={fillUnder(g.abandonPct, GOALS.abandonPct.value)}
            goalFraction={1} goalText={`Goal ${GOALS.abandonPct.label}`} beat={beatBy(g.abandonPct, GOALS.abandonPct)}
            footnote={`${fmtCount(g.abandons)} abandons`} />
        </div>
      )}

      {periods.length > 0 && <TeamRollup periods={periods} range={isRange} />}

      {d && periods.length > 0 && (
        <>
          <div style={s.row}>
            <input id="cc-team-search" type="search" aria-label="Search agents" placeholder="Search agents" value={search}
              onChange={(e) => setSearch(e.target.value)} style={{ ...s.input, minWidth: '200px' }} />
            {/* align="left" for the same reason the header's filters carry it:
                this row is left-aligned and in the page body, so a menu hung
                off the button's right edge opens under the sidebar. */}
            <SingleSelect label="Pod" options={agentPods} value={podPick}
              onChange={setAgentPod} allLabel="All pods" align="left" />
            <span style={{ marginLeft: 'auto' }}>
              <ExportButton path="/contact-center/team/export"
                params={{ start: d.range.start, end: d.range.end, ...choice, dept }}
                filename={`Contact-Center-Team_${d.range.start}_${d.range.end}.xlsx`} />
            </span>
          </div>
          <AgentTable agents={shown} periods={periods} onOpen={setOpenId} selectedId={openId} groupByPod={ranked || pods.length !== 1}
            holdTotal={isRange} />
          <p style={s.foot}>
            Agents are grouped by pod and ranked within it by {periods[0].label.toLowerCase()} Handled; in a pod of
            more than six, the top three are green and the bottom three amber. The Pod filter above narrows this
            table to one pod and changes nothing else on the page &mdash; the gauges and the rollup keep answering
            for the pods chosen in the header. The channel choice changes the
            gauges only, never which agents are listed. Team Efficiency weights each agent&rsquo;s Efficiency by
            their time logged in, over agents who have one.
          </p>
        </>
      )}

      {openId && d && <AgentDetail userId={openId} range={d.range} dept={dept} onClose={close} />}
    </div>
  );
}

const s = {
  page:    { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  row:     { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  filters: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  seg:     { display: 'inline-flex', gap: '2px', background: c.surfaceSunk, borderRadius: '8px', padding: '3px' },
  segOff:  { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: c.textMuted },
  segOn:   { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: c.textStrong, background: c.surface, boxShadow: '0 1px 2px rgba(15,23,42,0.08)' },
  goalRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', flexWrap: 'wrap' },
  goalPill:{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: c.surfaceSunk, color: c.textMuted, borderRadius: '999px', padding: '2px 8px' },
  goalDot: { width: '6px', height: '6px', borderRadius: '999px', flexShrink: 0 },
  label:   { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted, minWidth: '64px' },
  pillBtn: { ...btn.secondaryXs },
  pillOn:  { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  input:   { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface },
  gauges:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px' },
  gauge:   { ...card.base, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 },
  gTitle:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.textMuted },
  gValue:  { fontSize: '26px', fontWeight: 650, letterSpacing: '-0.02em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  gSub:    { fontSize: '11px', color: c.textMuted },
  pill:    { alignSelf: 'flex-start', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '999px' },
  chip:    { fontSize: '11px', color: c.textMuted },
  notice:  { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:   { fontSize: '13px', color: c.dangerText },
  muted:   { fontSize: '12px', color: c.textMuted, margin: 0 },
  foot:    { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
};
